import React from 'react';
import { TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { PlusCircleIcon, TrashIcon, Bars3Icon } from '@heroicons/react/24/outline';
import Button from '../../../../components/Button';
import { MultiLineInput } from '../../../../components/⚛️atoms/MultiLineInput';

const types = ['content', 'radio', 'input', 'textarea', 'checkbox', 'rating'];

const ToolItemForm = ({
  item,
  setItem,
  isEditing,
  isSubmitting,
  validateItem,
  handleSubmitEditItem,
  handleAddItem,
  handleCancelForm
}) => {
  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItem((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-6 bg-pdteal/5 border-2 border-pdteal rounded-lg flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold">{isEditing ? 'Edit Item' : 'Add New Item'}</h4>
      </div>

      <FormControl fullWidth>
        <InputLabel>Item Type</InputLabel>
        <Select
          name="type"
          value={item.type}
          onChange={handleItemChange}
          label="Item Type"
        >
          {types.map((itemType) => (
            <MenuItem key={itemType} value={itemType}>
              {itemType}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* For content type, show markdown textarea */}
      {item.type === 'content' ? (
        <MultiLineInput
          value={item.title}
          onChange={(value) => setItem((prev) => ({ ...prev, title: value }))}
          placeholder="Add formatted content using markdown..."
          lines={10}
        />
      ) : (
        <>
          <TextField
            label="Question / Title"
            name="question"
            value={item.question || ''}
            onChange={handleItemChange}
            fullWidth
            placeholder="Enter the question / title"
          />

          {/* For input type, show a placeholder/default value field */}
          {item.type === 'input' && (
            <TextField
              label="Placeholder Text (Optional)"
              name="placeholder"
              value={item.placeholder || ''}
              onChange={handleItemChange}
              fullWidth
              placeholder="Enter placeholder text that users will see"
              helperText="This text will appear in the input field as a hint"
            />
          )}

          {/* For rating type, show min/max rating fields */}
          {item.type === 'rating' && (
            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="Minimum Rating"
                name="minRating"
                type="number"
                inputProps={{ min: 0 }}
                value={item.minRating || 0}
                onChange={handleItemChange}
                fullWidth
                helperText="Starting value (e.g., 0 or 1)"
              />
              <TextField
                label="Maximum Rating"
                name="maxRating"
                type="number"
                value={item.maxRating || 10}
                onChange={handleItemChange}
                fullWidth
                helperText="Ending value (e.g., 5 or 10)"
              />
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-2 gap-4">
        <TextField
          label="Priority"
          name="priority"
          type="number"
          value={item.priority}
          onChange={handleItemChange}
          fullWidth
        />
        <TextField
          label="Points"
          name="points"
          type="number"
          value={item.points}
          onChange={handleItemChange}
          fullWidth
        />
      </div>

      <TextField
        label="Subtype (Optional)"
        name="subtype"
        value={item.subtype}
        onChange={handleItemChange}
        fullWidth
        placeholder="e.g., A, B, C"
      />

      {/* Options for radio/checkbox types */}
      {(item.type === 'radio' || item.type === 'checkbox') && (
        <div className="flex flex-col gap-4">
          <label className="text-base font-medium">Options</label>
          <div className="flex flex-col gap-3">
            {Array.isArray(item.options) && item.options.length > 0 ? (
              item.options.map((option, index) => {
                // Support both old format (string) and new format (object)
                const optionText = typeof option === 'string' ? option : option.text || '';
                const optionSubtype = typeof option === 'string' ? '' : option.subtype || '';

                return (
                  <div key={index} className="flex items-center gap-3">
                    <Bars3Icon className="w-6 h-6 text-gray-400 cursor-move flex-shrink-0" />
                    <TextField
                      value={optionText}
                      onChange={(e) => {
                        const newOptions = [...item.options];
                        newOptions[index] = typeof option === 'string'
                          ? { text: e.target.value, subtype: '' }
                          : { ...option, text: e.target.value };
                        setItem(prev => ({ ...prev, options: newOptions }));
                      }}
                      placeholder={`Option ${index + 1}`}
                      size="small"
                      className="flex-1"
                    />
                    <TextField
                      value={optionSubtype}
                      onChange={(e) => {
                        const newOptions = [...item.options];
                        newOptions[index] = typeof option === 'string'
                          ? { text: option, subtype: e.target.value }
                          : { ...option, subtype: e.target.value };
                        setItem(prev => ({ ...prev, options: newOptions }));
                      }}
                      placeholder="Subtype"
                      size="small"
                      style={{ width: '120px' }}
                      className="flex-shrink-0"
                    />
                    <TrashIcon
                      className="w-6 h-6 text-red-500 cursor-pointer hover:text-red-700 flex-shrink-0"
                      onClick={() => {
                        const newOptions = item.options.filter((_, i) => i !== index);
                        setItem(prev => ({ ...prev, options: newOptions }));
                      }}
                    />
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 italic">No options yet. Click below to add.</p>
            )}
            <button
              type="button"
              onClick={() => {
                const newOptions = Array.isArray(item.options)
                  ? [...item.options, { text: '', subtype: '' }]
                  : [{ text: '', subtype: '' }];
                setItem(prev => ({ ...prev, options: newOptions }));
              }}
              className="flex items-center cursor-pointer mt-4 text-teal-600 hover:text-teal-800 self-start"
            >
              <PlusCircleIcon className="w-6 h-6" />
              <span className="ml-2">Add Option</span>
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-4 justify-end">
        <Button outline onClick={handleCancelForm}>
          Cancel
        </Button>
        <Button
          onClick={isEditing ? handleSubmitEditItem : handleAddItem}
          disabled={isSubmitting || !validateItem()}
        >
          {isEditing ? 'Update Item' : 'Add Item'}
        </Button>
      </div>
    </div>
  );
};

export default ToolItemForm;

